import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useLocation } from "react-router-dom";

const INACTIVITY_LIMIT = 1 * 60 * 1000; // 1 minute
const WARNING_SECONDS = 60;
const MAX_SESSION = 8 * 60 * 60 * 1000; // 8 hours

export default function SessionTimeout({ onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_SECONDS);
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  const inactivityTimer = useRef(null);
  const warningInterval = useRef(null);
  const maxSessionTimer = useRef(null);
  const bc = useRef(null);

  const location = useLocation();

  /* -------------------- INIT -------------------- */
  useEffect(() => {
    if (isLoggedOut || location.pathname === "/login") return;

    bc.current = new BroadcastChannel("session_channel");

    initializeTimers();
    setupListeners();

    return cleanupAll;
  }, [isLoggedOut]);

  /* -------------------- TIMERS -------------------- */
  const initializeTimers = () => {
    if (!localStorage.getItem("LOGIN_TIME")) {
      localStorage.setItem("LOGIN_TIME", Date.now());
    }
    startInactivityTimer();
    startMaxSessionTimer();
  };

  const startInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(startWarning, INACTIVITY_LIMIT);
  };

  const startMaxSessionTimer = () => {
    const loginTime = Number(localStorage.getItem("LOGIN_TIME"));
    const elapsed = Date.now() - loginTime;

    if (elapsed >= MAX_SESSION) {
      forceLogout();
      return;
    }

    maxSessionTimer.current = setTimeout(forceLogout, MAX_SESSION - elapsed);
  };

  /* -------------------- WARNING -------------------- */
  const startWarning = () => {
    if (isLoggedOut) return;

    setShowWarning(true);
    setCountdown(WARNING_SECONDS);

    clearInterval(warningInterval.current);
    warningInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(warningInterval.current);
          forceLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetWarning = () => {
    setShowWarning(false);
    clearInterval(warningInterval.current);
    startInactivityTimer();
  };

  /* -------------------- EVENTS -------------------- */
  const handleUserActivity = () => {
    if (isLoggedOut) return;

    if (showWarning) resetWarning();
    else startInactivityTimer();
  };

  const setupListeners = () => {
    ["mousemove", "click", "keydown", "scroll", "touchstart"].forEach((e) =>
      window.addEventListener(e, handleUserActivity)
    );

    window.addEventListener("storage", onStorageReceive);

    bc.current.onmessage = (msg) => {
      if (msg.data === "logout") forceLogout(false);
    };
  };

  const onStorageReceive = (e) => {
    if (e.key === "LOGOUT_TRIGGER") forceLogout(false);
  };

  /* -------------------- CLEANUP -------------------- */
  const cleanupAll = () => {
    ["mousemove", "click", "keydown", "scroll", "touchstart"].forEach((e) =>
      window.removeEventListener(e, handleUserActivity)
    );

    window.removeEventListener("storage", onStorageReceive);

    clearTimeout(inactivityTimer.current);
    clearTimeout(maxSessionTimer.current);
    clearInterval(warningInterval.current);

    try {
      bc.current?.close();
    } catch {
      /* empty */
    }
  };

  /* -------------------- LOGOUT -------------------- */
  const forceLogout = (trigger = true) => {
    if (isLoggedOut) return;

    setIsLoggedOut(true);
    setShowWarning(false);

    if (trigger) {
      bc.current?.postMessage("logout");
      localStorage.setItem("LOGOUT_TRIGGER", Date.now());
    }

    cleanupAll();
    onLogout();
  };

  if (isLoggedOut) return null;

  /* -------------------- UI -------------------- */
  return (
    <>
      {showWarning && (
        <Modal show centered backdrop="static">
          <Modal.Header>
            <Modal.Title>Session Timeout Warning</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            You will be logged out in <b>{countdown}</b> seconds due to inactivity.
          </Modal.Body>

          <Modal.Footer>
            <Button variant="primary" onClick={resetWarning}>
              Continue Session
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
